// Путь: D:\QR\QR-WEB\dashboard\src\components\Admin\AdminWallets\AdminWallets.jsx
// Название: Admin Brand Wallets Management
// Версия: 1.0.0
// Дата: 26.01.2026

import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import './AdminWallets.css';

const AdminWallets = () => {
  const [wallets, setWallets] = useState([]);
  const [stats, setStats] = useState({ 
    total: 0, 
    totalDeposit: 0, 
    totalAvailable: 0, 
    totalReserved: 0,
    totalFees: 0 
  });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/wallets');
      if (res.data.success) {
        setWallets(res.data.wallets || []);
        setStats(res.data.stats || {});
      }
    } catch (error) {
      console.error('Load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatEur = (amount) => {
    const num = parseFloat(amount) || 0;
    return `€${num.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getStatusBadge = (wallet) => {
    const available = parseFloat(wallet.available_balance) || 0;
    const deposit = parseFloat(wallet.deposit_balance) || 0;
    
    if (deposit === 0) {
      return <span className="admin-badge badge-default">Пустой</span>;
    }
    if (available > 500) {
      return <span className="admin-badge badge-success">Активный</span>;
    }
    if (available > 0) {
      return <span className="admin-badge badge-warning">Низкий</span>;
    }
    return <span className="admin-badge badge-danger">Исчерпан</span>;
  };

  const filteredWallets = wallets.filter(w => {
    const available = parseFloat(w.available_balance) || 0;
    const deposit = parseFloat(w.deposit_balance) || 0;
    
    if (filter === 'all') return true;
    if (filter === 'active') return available > 0;
    if (filter === 'empty') return deposit === 0;
    if (filter === 'low') return available > 0 && available <= 100;
    return true;
  });

  if (loading) {
    return (
      <div className="admin-wallets">
        <div className="admin-loading">⏳ Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="admin-wallets">
      <div className="admin-page-header">
        <h1>💰 Brand Wallets</h1>
        <p>Балансы бизнесов для кампаний</p>
      </div>

      {/* Stats Cards */}
      <div className="admin-stats-grid">
        <div className="admin-stat-card">
          <div className="stat-icon">🏦</div>
          <div className="stat-info">
            <span className="stat-value">{stats.total || wallets.length}</span>
            <span className="stat-label">Кошельков</span>
          </div>
        </div>
        <div className="admin-stat-card success">
          <div className="stat-icon">💵</div>
          <div className="stat-info">
            <span className="stat-value">{formatEur(stats.totalDeposit)}</span>
            <span className="stat-label">Всего депозитов</span>
          </div>
        </div>
        <div className="admin-stat-card info">
          <div className="stat-icon">💳</div>
          <div className="stat-info">
            <span className="stat-value">{formatEur(stats.totalAvailable)}</span>
            <span className="stat-label">Доступно</span>
          </div>
        </div>
        <div className="admin-stat-card warning">
          <div className="stat-icon">📦</div>
          <div className="stat-info">
            <span className="stat-value">{formatEur(stats.totalReserved)}</span>
            <span className="stat-label">В резерве</span>
          </div>
        </div>
      </div>

      {/* Revenue Card */}
      <div className="revenue-card">
        <div className="revenue-icon">📊</div>
        <div className="revenue-info">
          <span className="revenue-label">Комиссии DARY (CPL)</span>
          <span className="revenue-value">{formatEur(stats.totalFees)}</span>
        </div>
      </div>

      {/* Filters */}
      <div className="admin-filters">
        <button 
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          Все ({wallets.length})
        </button>
        <button 
          className={`filter-btn ${filter === 'active' ? 'active' : ''}`}
          onClick={() => setFilter('active')}
        >
          Активные
        </button>
        <button 
          className={`filter-btn ${filter === 'low' ? 'active' : ''}`}
          onClick={() => setFilter('low')}
        >
          Низкий баланс
        </button>
        <button 
          className={`filter-btn ${filter === 'empty' ? 'active' : ''}`}
          onClick={() => setFilter('empty')}
        >
          Пустые
        </button>
      </div>

      {/* Wallets Table */}
      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Бизнес</th>
              <th>Статус</th>
              <th>Депозит</th>
              <th>Доступно</th>
              <th>В кампаниях</th>
              <th>Комиссии</th>
              <th>Обновлён</th>
            </tr>
          </thead>
          <tbody>
            {filteredWallets.length === 0 ? (
              <tr>
                <td colSpan="8" className="empty-row">Нет кошельков</td>
              </tr>
            ) : (
              filteredWallets.map(wallet => (
                <tr key={wallet.id}>
                  <td>#{wallet.id}</td>
                  <td>
                    <div className="business-cell">
                      <span className="business-email">{wallet.business_email || `ID: ${wallet.business_id}`}</span>
                      {wallet.company_name && (
                        <span className="company-name">{wallet.company_name}</span>
                      )}
                    </div>
                  </td>
                  <td>{getStatusBadge(wallet)}</td>
                  <td className="text-success">{formatEur(wallet.deposit_balance)}</td>
                  <td className="text-info">{formatEur(wallet.available_balance)}</td>
                  <td className="text-warning">{formatEur(wallet.promo_reserve)}</td>
                  <td className="text-muted">{formatEur(wallet.fees_reserve)}</td>
                  <td className="text-muted">
                    {wallet.updated_at ? new Date(wallet.updated_at).toLocaleDateString('ru-RU') : '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminWallets;
